/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import React from 'react';
import { Form, FormGroup } from 'reactstrap';
import {
  GROUND_MOTION_METHOD,
  GROUND_MOTION_UPLOAD,
  HAZARD_EDITION,
  INCLUDE_DESIGN_INTENSITIES,
  RETURN_PERIODS,
  RETURN_PERIOD_DE,
  RETURN_PERIOD_MCE,
  SITE_CLASS,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import BackButton from '#components/pages/Models/Model/sections/support/buttons/BackButton.jsx';
import NextButton from '#components/pages/Models/Model/sections/support/buttons/NextButton.jsx';
import GridField from '#components/pages/Models/Model/sections/support/forms/GridField/index.jsx';
import SiteClassSelectMenu from '#components/pages/Models/Model/sections/support/forms/SiteClassSelectMenu/index.jsx';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import HazardEditionSelectMenu from '#components/pages/Models/Model/sections/support/forms/HazardEditionSelectMenu/index.js';
import GroundMotionMethodSelectMenu from './GroundMotionMethodSelectMenu/index.jsx';
import ReturnPeriodSelectMenu from './ReturnPeriodSelectMenu/index.js';
import GroundMotionTable from './GroundMotionTable/index.js';
import GroundMotionUploader from './GroundMotionUploader/index.js';
import SiteClassRange from './SiteClassRange/index.js';

type Props = {|
  form: string,
  heading: string,
  modelId: string,
  onBack: Function,
  onNext: Function,
  country: string,
  dbe: string,
  mce: string,
  groundMotionClosestVs30: string,
  showHazardEdition: boolean,
  showIncludeDesignIntensities: boolean,
  showDesignIntensities: boolean,
  showReturnPeriods: boolean,
  showReturnPeriodDEandMCE: boolean,
  showUploader: boolean,
  showGroundMotionTable: boolean,
|};

const GroundMotionSection = (props: Props): Element<'div'> => {
  const {
    heading,
    form,
    modelId,
    onBack,
    onNext,
    country,
    dbe,
    mce,
    groundMotionClosestVs30,
    showIncludeDesignIntensities,
    showDesignIntensities,
    showHazardEdition,
    showReturnPeriods,
    showReturnPeriodDEandMCE,
    showUploader,
    showGroundMotionTable,
  } = props;

  const showIntTooltip = !['JP', 'US'].includes(country);
  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate>
        <FormGroup tag="fieldset">
          <legend>Soil Information</legend>
          <GridField
            name={SITE_CLASS}
            form={form}
            options={{
              columnSize: 4,
              labelText: 'Site Class',
              ComponentType: SiteClassSelectMenu,
              includeToolTip: true,
            }}
          />
          <SiteClassRange form={form} />
        </FormGroup>
        <FormGroup tag="fieldset">
          <legend>
            Ground Motion Hazard Info{' '}
            <Tip tipId={showIntTooltip ? 'groundMotionInt' : 'groundMotion'} />
          </legend>
          <GridField
            name={GROUND_MOTION_METHOD}
            form={form}
            options={{
              columnSize: 5,
              labelText: 'Source',
              ComponentType: GroundMotionMethodSelectMenu,
              componentProps: {
                country,
              },
              includeToolTip: true,
              toolTipId: showIntTooltip ? 'groundMotionMethodInt' : GROUND_MOTION_METHOD,
            }}
          />
          {showHazardEdition && (
            <GridField
              name={HAZARD_EDITION}
              form={form}
              options={{
                columnSize: 5,
                labelText: 'Hazard Edition',
                ComponentType: HazardEditionSelectMenu,
                includeToolTip: true,
              }}
            />
          )}
          {showIncludeDesignIntensities && (
            <GridField
              name={INCLUDE_DESIGN_INTENSITIES}
              form={form}
              options={{
                columnSize: 6,
                labelText: 'Include Design Intensities?',
                ComponentType: 'checkbox',
                includeToolTip: true,
              }}
            />
          )}
          {showReturnPeriods && (
            <FormGroup tag="fieldset">
              <legend>
                Return Periods <Tip tipId={RETURN_PERIODS} />
              </legend>
              {showDesignIntensities && (
                <div id="designIntensities" className="pt-2 pb-3">
                  <div>
                    DE :<span className="pl-1 pr-1">{dbe}</span>
                    <Tip tipId={RETURN_PERIOD_DE} />
                  </div>
                  <div>
                    MCE :<span className="pl-1 pr-1">{mce}</span>
                    <Tip tipId={RETURN_PERIOD_MCE} />
                  </div>
                </div>
              )}
              <GridField
                name={RETURN_PERIODS}
                form={form}
                options={{
                  columnSize: 6,
                  labelText: showDesignIntensities ? 'Additional Return Periods' : 'Return Periods',
                  ComponentType: 'string',
                  includeToolTip: false,
                }}
              />
            </FormGroup>
          )}
          {showUploader && (
            <GroundMotionUploader
              form={form}
              name={GROUND_MOTION_UPLOAD}
              modelId={modelId}
              accept=".csv"
              maxBytes={3e6}
            />
          )}
          {showReturnPeriodDEandMCE && (
            <div id="designIntensityInputs">
              <FormGroup tag="fieldset">
                <legend>
                  Return Periods
                  <Tip tipId="returnPeriodsForGroundMotionUpload" />
                </legend>
                <GridField
                  name={RETURN_PERIOD_DE}
                  form={form}
                  options={{
                    columnSize: 3,
                    labelText: 'DE',
                    ComponentType: ReturnPeriodSelectMenu,
                    includeToolTip: true,
                    componentProps: {
                      modelId,
                    },
                  }}
                />
                <GridField
                  name={RETURN_PERIOD_MCE}
                  form={form}
                  options={{
                    columnSize: 3,
                    labelText: 'MCE',
                    ComponentType: ReturnPeriodSelectMenu,
                    includeToolTip: true,
                    componentProps: {
                      modelId,
                    },
                  }}
                />
              </FormGroup>
            </div>
          )}
          {showGroundMotionTable && (
            <div className="pt-2 pb-2">
              <GroundMotionTable modelId={modelId} />
              {groundMotionClosestVs30 && (
                <div>Closest VS30 for USGS Hazard Lookup (m/s): {groundMotionClosestVs30}</div>
              )}
            </div>
          )}
        </FormGroup>
        <BackButton onClick={onBack} />
        <NextButton onClick={onNext} />
      </Form>
    </div>
  );
};

export default GroundMotionSection;
